import type { PeopleGateway } from './PeopleGateway';
import type { EmployeeListPage, EmployeeResponse, ListEmployeesParams } from './types';

// The server clamps pageSize to this (see EmployeeService.ListAsync) — requesting more just gets clamped
// back down, so this is the most per page a single request can return.
const LOAD_ALL_PAGE_SIZE = 100;

/**
 * For callers that genuinely need the complete employee collection — a selection picker (Custom Pay Run,
 * Time & Attendance, Time Off, Employee Pay Summary), not the paginated Employee List screen itself.
 *
 * The Employee List query (EPN-38) is server-paginated with a maximum page size; a caller that just asks
 * for one page at that maximum would silently truncate once a company has more employees than the
 * ceiling. This instead follows every page until the full `totalCount` has been collected, so a picker's
 * "give me everyone" behavior keeps working regardless of company size.
 *
 * The primary Employee List page must NOT use this — it stays server-paginated, one page at a time, by
 * design.
 */
export async function listAllEmployees(
  gateway: PeopleGateway,
  params: Omit<ListEmployeesParams, 'page' | 'pageSize'> = {},
): Promise<EmployeeResponse[]> {
  const all: EmployeeResponse[] = [];
  let page = 1;
  let totalCount = Number.POSITIVE_INFINITY;

  while (all.length < totalCount) {
    const result: EmployeeListPage = await gateway.listEmployees({ ...params, page, pageSize: LOAD_ALL_PAGE_SIZE });
    all.push(...result.items);
    totalCount = result.totalCount;
    // A server response that returns no items despite claiming more exist would otherwise loop forever —
    // this is the safety net for that case, not an expected path.
    if (result.items.length === 0) {
      break;
    }
    page += 1;
  }

  return all;
}
